import {
	createContext,
	ReactNode,
	useContext,
	useState,
	Dispatch,
	SetStateAction,
} from "react";
import { IOptionProps } from "../@types/reactSelect/IOption";
import { IBatch } from "../@types/API/IBatches";

export type FilterV2Selection = IOptionProps | IOptionProps[] | null;

export type FilterContextV2Value = {
	selectedFarms: FilterV2Selection;
	setSelectedFarms: Dispatch<SetStateAction<FilterV2Selection>>;
	selectedAreas: FilterV2Selection;
	setSelectedAreas: Dispatch<SetStateAction<FilterV2Selection>>;
	catalogMicroAreas: IMicroArea[];
	setCatalogMicroAreas: Dispatch<SetStateAction<IMicroArea[]>>;
	catalogBatches: IBatch[];
	setCatalogBatches: Dispatch<SetStateAction<IBatch[]>>;
};

const FilterContextV2 = createContext<FilterContextV2Value | undefined>(
	undefined,
);

type FilterContextV2ProviderProps = {
	children: ReactNode;
};

/** Converte seleção do react-select em lista de ids (string). */
export function selectionToIdStrings(selection: FilterV2Selection): string[] {
	if (selection == null) return [];
	const opts = Array.isArray(selection) ? selection : [selection];
	return opts.map((o) => String(o.value));
}

export function flattenSelection(s: FilterV2Selection): IOptionProps[] {
	if (s == null) return [];
	return Array.isArray(s) ? s : [s];
}

/** IDs numéricos para query params e APIs. */
export function selectionToNumericIds(s: FilterV2Selection): number[] {
	return flattenSelection(s).map((o) => Number(o.value));
}

/** Chave estável para dependências de efeitos (evita loops por referência). */
export function selectionToSortedIdsKey(s: FilterV2Selection): string {
	return selectionToIdStrings(flattenSelection(s)).slice().sort().join(",");
}

/** Primeira fazenda selecionada (APIs legadas com `farmId` singular). */
export function primaryFarmIdFromSelection(
	selectedFarms: FilterV2Selection,
): number | undefined {
	const ids = selectionToNumericIds(selectedFarms);
	return ids[0];
}

export function FilterContextV2Provider({
	children,
}: FilterContextV2ProviderProps) {
	const [selectedFarms, setSelectedFarms] = useState<FilterV2Selection>(null);
	const [selectedAreas, setSelectedAreas] = useState<FilterV2Selection>(null);
	const [catalogMicroAreas, setCatalogMicroAreas] = useState<IMicroArea[]>([]);
	const [catalogBatches, setCatalogBatches] = useState<IBatch[]>([]);

	return (
		<FilterContextV2.Provider
			value={{
				selectedFarms,
				setSelectedFarms,
				selectedAreas,
				setSelectedAreas,
				catalogMicroAreas,
				setCatalogMicroAreas,
				catalogBatches,
				setCatalogBatches,
			}}
		>
			{children}
		</FilterContextV2.Provider>
	);
}

export function useFilterContextV2(): FilterContextV2Value {
	const ctx = useContext(FilterContextV2);
	if (ctx === undefined) {
		throw new Error(
			"useFilterContextV2 must be used within a FilterContextV2Provider",
		);
	}
	return ctx;
}
